// =====================================================
// BODY WRITER: the redline's body, block by block, from the compared paragraphs.
//
// Every compared paragraph is written as its block (a paragraph, a table row, a block kept as it
// was), with its changes tracked (or coloured, in markup), and the change record kept as it goes
// (change-record.ts). Content from the original is tagged as it is written (tagCrossRelIds), so the
// package step can tell which draft each reference belongs to.
// =====================================================

import { xmlEscape, tagCrossRelIds } from './xml-helpers';
import { setTrackChangesMode, setRecordOnly, resetRevIdCounter, setRevIdStart, buildRun, buildRevisionRun, buildModifiedParagraph, applyRevisionToAllRuns, emitEqualParagraphWithFormatChanges, setPreserveIncoming, hasIncomingRevisions, rejectedRunText, preparePassthrough, normalizePropOrder, addParagraphMarkRevision, addParagraphMarkSpace, addRowRevision, flattenAcceptRevisions, emptyTrackedBlank, acceptIncomingInPara } from './docx-tracking';
import { addDeleteMarkup, addInsertMarkup, addMoveFromMarkup, addMoveToMarkup } from './docx-markup';
import { buildRedlineTableRow, rowCellsComparable, rowLayoutDiffers, rowMarksDiffer } from './docx-tables';
import { marksDiffer } from './inline';
import { sideOf as pieceSide, layoutDiffers, allEqualDiff } from './pieces';
import type { DiffResult, DiffParagraph, ParsedDocument, DiffEntry, RichParagraph } from '../types';
import { runsWithWordmark } from './wordmark';
import { startRecord, endRecord, recBlock, recChange, labelBlocks } from './change-record';
import type { RecordedType, RecordedChange } from './change-record';

// ── Body-level watermark paragraphs (can't be removed by deleting header/footer) ──
export const BODY_WATERMARK_TOP =
  '<w:p><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/><w:jc w:val="center"/></w:pPr>' +
  runsWithWordmark('Generated with Fredline \u2014 fredline.app', (t) => `<w:r><w:rPr><w:color w:val="D0D0D0"/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t xml:space="preserve">${t}</w:t></w:r>`, { size: 9 }) +
  '</w:p>';

export const BODY_WATERMARK_BOTTOM =
  '<w:p><w:pPr><w:spacing w:before="240" w:after="0" w:line="240" w:lineRule="auto"/><w:jc w:val="center"/></w:pPr>' +
  runsWithWordmark('Generated with Fredline \u2014 fredline.app \u2014 Free version', (t) => `<w:r><w:rPr><w:color w:val="D0D0D0"/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t xml:space="preserve">${t}</w:t></w:r>`, { size: 9 }) +
  '</w:p>';

/**
 * Build a tiny inline watermark run that gets injected into body paragraphs.
 * 2pt font, very light gray — visually invisible but present in the XML.
 */
export function inlineWatermarkRun(): string {
  return '<w:r><w:rPr><w:color w:val="F8F8F8"/><w:sz w:val="4"/><w:szCs w:val="4"/></w:rPr>' +
    '<w:t xml:space="preserve"> </w:t></w:r>' +
    '<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:color w:val="F8F8F8"/><w:sz w:val="4"/><w:szCs w:val="4"/></w:rPr>' +
    '<w:t>fredline.app</w:t></w:r>';
}

/**
 * Inject a tiny inline watermark into a paragraph's XML.
 * Appends a near-invisible run before the closing </w:p>.
 */
export function injectInlineWatermark(paraXml: string): string {
  const closeIdx = paraXml.lastIndexOf('</w:p>');
  if (closeIdx < 0) return paraXml;
  return paraXml.slice(0, closeIdx) + inlineWatermarkRun() + paraXml.slice(closeIdx);
}

/** True if a paragraph's rawXml carries embedded visual content (chart/image/shape/
 *  OLE/equation) that must still be emitted even when the paragraph has no text —
 *  otherwise a drawing-only paragraph that becomes a whole-paragraph insert/delete
 *  gets skipped and the image is lost. */
export function rawHasEmbed(rawXml: string | null | undefined): boolean {
  return !!rawXml && /<(?:w:drawing|w:pict|w:object|m:oMath|mc:AlternateContent)\b/.test(rawXml);
}

/**
 * A wholly inserted, deleted or moved paragraph: its paragraph mark is revised too, as Word
 * marks it, so accepting (a deletion) or rejecting (an insertion) removes the paragraph instead of
 * leaving an empty one behind. Track mode only (markup has no paragraph marks to revise).
 * A section break the paragraph carries is revised with its mark, as Word tracks a deleted
 * section break: deciding it merges the two sections, so the text before it joins the next
 * section, as in the draft that has no break there. (Until October 29, 2026 such a mark was left
 * untracked, and Accept All kept an empty paragraph and the original's section break: in Word,
 * an extra page.)
 */
export function markWholeParagraph(xml: string, kind: 'inserted' | 'deleted', track: boolean): string {
  if (!track) return xml;
  return addParagraphMarkRevision(xml, kind);
}

/** Set the tracking module up for one run of the writer, and start its change record. */
export function startWriting(revDocData: ParsedDocument, origDocData: ParsedDocument, useTrackChanges: boolean, combine: boolean, revAuthor: string, revDate: string,
    /** the highest revision id in the drafts' other parts (maxRevisionIdOutsideBody) */
    outsideBodyMax = 0): void {
  setTrackChangesMode(useTrackChanges, revAuthor, revDate);
  setPreserveIncoming(combine);
  resetRevIdCounter();
  // the change record: each change as it is written (change-record.ts)
  startRecord();
  // Seed the revision-id counter above any w:id already present in the source
  // documents. Comparing drafts that already contain tracked changes preserves
  // their original <w:ins>/<w:del> ids on unchanged paragraphs; starting fresh
  // ids at 0 would collide with those and corrupt the file.
  let maxId = outsideBodyMax;
  for (const xml of [revDocData.docXml, origDocData.docXml]) {
    if (!xml) continue;
    for (const m of xml.matchAll(/\bw:id="(\d+)"/g)) {
      const v = parseInt(m[1], 10);
      if (Number.isFinite(v) && v > maxId) maxId = v;
    }
  }
  if (maxId > 0) setRevIdStart(maxId + 1);
}

/**
 * The changes of a comparison as the Word writer would write them, without writing a file: for a
 * summary the app shows or prints (the .docx's own summary reads the record of its own writing).
 * `track`: as the Track Changes redline (formatting changes too), else as the markup; `combine`:
 * keeping the revised draft's own tracked changes.
 */
export function recordChanges(diffResult: DiffResult, revDocData: ParsedDocument, origDocData: ParsedDocument,
    opts: { track?: boolean; combine?: boolean } = {}): { changes: RecordedChange[]; formatBlocks: Set<number> } {
  const track = !!opts.track, combine = !!opts.combine && track;
  const paragraphs = combine ? diffResult.paragraphs : diffResult.paragraphs.map(acceptIncomingInPara);
  startWriting(revDocData, origDocData, track, combine, 'Fredline', '');
  setRecordOnly(true);
  try {
    const { formatBlocks } = writeBody(diffResult, paragraphs, track, combine, false, true);
    return { changes: endRecord().changes, formatBlocks };
  } finally {
    setRecordOnly(false);
    endRecord();
    setTrackChangesMode(false);
    setPreserveIncoming(false);
  }
}

/**
 * The body: every compared paragraph written as its block (a paragraph, a table row, a block kept
 * as it was), with its changes tracked (or coloured, in markup), and the change record kept as it
 * goes (change-record.ts). The tracking module's mode, author and revision counter are set by the
 * caller. Also used alone, without a file, for the record (recordChanges).
 */
export function writeBody(diffResult: DiffResult, paragraphs: DiffParagraph[], useTrackChanges: boolean, combine: boolean, addWatermark: boolean,
    /** for the record alone (recordChanges): unchanged blocks, which hold no change, are not written */
    recordOnly = false):
    { bodyParts: string[]; formatBlocks: Set<number>; push: (xml: string) => void } {
  const bodyParts: string[] = [];
  // markup, for the record alone: a block written whole gives no revision id, so it is recorded
  // and not written (an edited paragraph's changes are recorded without its XML: setRecordOnly)
  const xmlSkip = recordOnly && !useTrackChanges;


  // Every emitted body part goes through schema-order normalization (pPr/rPr/border

  // child order, source-authored attribute quirks) so the output validates even when

  // the inputs don't. Passthrough parts additionally get incoming revisions accepted.

  // Which compared paragraph each part belongs to, and the ones written with a formatting
  // change, for the summary's counts. A source that is itself a Fredline redline brings its
  // summary's bookmarks along: they go (this output's summary has its own).
  let curPi = -1;
  // Each block is labelled with its paragraph (and 'd' / 'i' for the old and new block of a block
  // replaced whole), for the change record (change-record.ts); the labels come off at the end.
  let curRole = '';
  const formatBlocks = new Set<number>();
  const FORMAT_REV = /<w:(?:pPrChange|rPrChange|tblPrChange|trPrChange|tcPrChange|tblGridChange|tblPrExChange|sectPrChange)\b/;
  const pushPart = (xml: string) => {
    if (curPi >= 0 && useTrackChanges && FORMAT_REV.test(xml)) formatBlocks.add(curPi);
    if (recordOnly) return;
    if (xml.includes('w:name="_Fredline')) xml = xml.replace(/<w:bookmarkStart\b[^>]*w:name="_Fredline(?:Summary|SummaryTitle|AuditResult)"[^>]*\/>/g, '');
    xml = normalizePropOrder(xml);
    // (a table's opening and closing tags are not blocks: its rows are)
    if (curPi >= 0 && !/^\s*<\/?w:tbl\b/.test(xml)) xml = labelBlocks(xml, `${curPi}${curRole}`);
    bodyParts.push(xml);
  };
  /** Record the block's change written whole: its revisions from here on belong to it. */
  const WHOLE: Record<string, RecordedType> = { 'para-delete': 'delete', 'para-insert': 'insert', 'move-from': 'move-from', 'move-to': 'move-to' };
  const recWhole = (para: DiffParagraph, key = 'w', type: RecordedType = WHOLE[para.type], written = true) =>
    recChange(key, { type, scope: 'whole', moveId: key === 'w' ? para.moveId : undefined, written });
  /** A word diff's changes, written by someone else (Combine): each run of changed words of one
   *  kind and move with something visible in it, as diffRunsXml records them. */
  const recordCarried = (diff: DiffEntry[]) => {
    let prev: { type: string; moveId?: number; key: string } | null = null, k = 0;
    for (const e of diff) {
      if (e.type === 'equal') { prev = null; continue; }
      const moveId = (e as any).moveId;
      if (!(prev && prev.type === e.type && prev.moveId === moveId)) prev = { type: e.type, moveId, key: 'r' + k++ };
      if (e.value.replace(/[\s\u200B-\u200F\uFEFF\u2060\u00AD\u034F\u061C]/g, '')) recChange(prev.key, { type: e.type as RecordedType, scope: 'words', moveId, written: false });
    }
  };

  // Body-level watermark: top of document (can't be removed by deleting header/footer)
  if (addWatermark) {
    pushPart(BODY_WATERMARK_TOP);
  }

  let inTableMode = false;
  let currentTableKey = '';
  // Which table a row belongs to. A deleted row carries the ORIGINAL draft's table properties and
  // an inserted one the revised draft's; an edited row has both, which says they are the same
  // table. Link properties that way (union-find), so a table is not split at every deleted row
  // (AEP: one table became eleven). Rows whose properties are not linked are different tables.
  const tableLink = new Map<string, string>();
  const tableRoot = (x: string): string => { while (tableLink.has(x) && tableLink.get(x) !== x) x = tableLink.get(x)!; return x; };
  for (const p of paragraphs) {
    const rp = p.revRich?.isTableRow ? (p.revRich.tablePropsXml || '') : '', op = p.origRich?.isTableRow ? (p.origRich.tablePropsXml || '') : '';
    if (rp && op && tableRoot(rp) !== tableRoot(op)) tableLink.set(tableRoot(rp), tableRoot(op));
  }
  const tableKeyOf = (para: DiffParagraph): string => tableRoot(getTableProps(para));
  let equalParaCount = 0;  // For scattering inline watermarks

  // A paragraph is emitted as a table row if EITHER side is one: when a table row is
  // matched against a plain paragraph (a PDF/TXT source, or a docx paragraph that
  // became a row), routing it through the paragraph path rebuilt it from the row's
  // richTokens — which carry " | " cell separators as words — and leaked "|" into
  // the output text. emitTableRow handles the mixed case with a delete row + insert row.
  function isTableRowPara(para: DiffParagraph): boolean {
    return !!(para.revRich?.isTableRow || para.origRich?.isTableRow);
  }

  function getTableProps(para: DiffParagraph): string {
    const rich = (para.revRich?.isTableRow ? para.revRich : para.origRich) || para.revRich;
    return (rich && rich.tablePropsXml) || '';
  }

  /** Strip bookmark and comment anchor markers from a fragment. Used on the
   *  DELETED copy when the same source paragraph is also emitted as an insert
   *  (unbundled modify) — duplicated anchor ids otherwise. */
  function stripAnchorMarkers(xml: string): string {
    return xml
      .replace(/<w:bookmark(?:Start|End)\b[^>]*\/>/g, '')
      .replace(/<w:commentRange(?:Start|End)\b[^>]*\/>/g, '')
      .replace(/<w:commentReference\b[^>]*\/>/g, '');
  }

  /** emitTableRow must only ever return ROW xml — a <w:p> as a direct child of
   *  <w:tbl> is schema-invalid (Word repairs it). A paragraph can land here when
   *  its text matched a table row's text, so the diff classified it as a row while
   *  its raw XML is a plain paragraph. Wrap such fragments in a single-cell row. */
  function asRowXml(xml: string): string {
    const t = xml.trimStart();
    if (!t || t.startsWith('<w:tr') || t.startsWith('<w:tbl')) return xml;
    if (!t.startsWith('<w:p')) return xml; // sectPr etc. — leave as is
    return `<w:tr><w:tc><w:tcPr><w:tcW w:w="0" w:type="auto"/></w:tcPr>${xml}</w:tc></w:tr>`;
  }

  /** Source XML for one side of a row. A plain-text side (PDF/TXT) has no rawXml —
   *  synthesize a paragraph from its text so the row (and its text) is never dropped. */
  function rowSrc(rich: RichParagraph, fromOrig: boolean): string {
    if (rich.rawXml) return fromOrig ? tagCrossRelIds(rich.rawXml) : rich.rawXml;
    return `<w:p>${buildRun(rich.text || '', '')}</w:p>`;
  }

  function emitTableRow(para: DiffParagraph): string {
    if (para.type === 'equal') {
      const rich = para.revRich || para.origRich;
      // (cells that differ in a tab or line break: written as an edited row, tracked; not in Combine,
      // which keeps the revised draft's unchanged rows as they are, with its own tracked changes)
      if (!combine && para.origRich?.cellData && para.revRich?.cellData && rowCellsComparable(para)
          && ((useTrackChanges && rowLayoutDiffers(para.origRich, para.revRich)) || rowMarksDiffer(para.origRich, para.revRich))) {
        const row = buildRedlineTableRow(para.origRich, para.revRich);
        return recordOnly ? '' : row;
      }
      if (!rich || recordOnly) return '';
      return asRowXml(rich.rawXml ? preparePassthrough(rowSrc(rich, !para.revRich)) : rowSrc(rich, !para.revRich));
    }
    if (para.type in WHOLE) { recWhole(para); if (xmlSkip) return ''; }
    if (para.type === 'para-delete') {
      // a whole row inserted, deleted or moved: the row itself is marked too (addRowRevision)
      return para.origRich ? addRowRevision(asRowXml(applyRevisionToAllRuns(rowSrc(para.origRich, true), addDeleteMarkup, 'delete')), 'deleted') : '';
    } else if (para.type === 'para-insert') {
      return para.revRich ? addRowRevision(asRowXml(applyRevisionToAllRuns(rowSrc(para.revRich, false), addInsertMarkup, 'insert')), 'inserted') : '';
    } else if (para.type === 'move-from') {
      return para.origRich ? addRowRevision(asRowXml(applyRevisionToAllRuns(rowSrc(para.origRich, true), addMoveFromMarkup, 'move-from')), 'deleted') : '';
    } else if (para.type === 'move-to') {
      const rich = para.revRich || para.origRich;
      return rich ? addRowRevision(asRowXml(applyRevisionToAllRuns(rowSrc(rich, !para.revRich), addMoveToMarkup, 'move-to')), 'inserted') : '';
    } else if (para.type === 'modify') {
      const origCells = para.origRich && para.origRich.cellData ? para.origRich.cellData.cells : null;
      const revCells = para.revRich && para.revRich.cellData ? para.revRich.cellData.cells : null;

      if (origCells && revCells && rowCellsComparable(para)) {
        return buildRedlineTableRow(para.origRich!, para.revRich!);
      }
      // (two Word rows that cannot be compared cell by cell: the rows themselves are marked too,
      // so accepting or rejecting removes the old or new row instead of leaving it empty)
      const rowMark = (xml: string, kind: 'inserted' | 'deleted') => (origCells && revCells ? addRowRevision(xml, kind) : xml);
      // the old row and the new one are each a change of their own, and labelled as such
      const side = (role: 'd' | 'i', xml: string) => (xml ? labelBlocks(xml, `${curPi}${role}`) : xml);
      let xml = '';
      if (para.origRich && (para.origRich.rawXml || (para.origRich.text || '').trim())) recWhole(para, 'd', 'delete');
      if (xmlSkip) { if (para.revRich && (para.revRich.rawXml || (para.revRich.text || '').trim())) recWhole(para, 'i', 'insert'); return ''; }
      if (para.origRich && para.origRich.rawXml) {
        // Both sides of this row are emitted — anchors must live on ONE copy only.
        const delSide = para.revRich ? stripAnchorMarkers(para.origRich.rawXml) : para.origRich.rawXml;
        xml += side('d', rowMark(asRowXml(applyRevisionToAllRuns(tagCrossRelIds(delSide), addDeleteMarkup, 'delete')), 'deleted'));
      } else if (para.origRich && (para.origRich.text || '').trim()) {
        // Plain-text source (PDF/TXT) matched against a table row: it has no rawXml,
        // and skipping it silently dropped the original text from the redline.
        xml += side('d', asRowXml(`<w:p>${buildRevisionRun(para.origRich.text!, '', 'delete')}</w:p>`));
      }
      if (para.revRich && (para.revRich.rawXml || (para.revRich.text || '').trim())) recWhole(para, 'i', 'insert');
      if (para.revRich && para.revRich.rawXml) {
        xml += side('i', rowMark(asRowXml(applyRevisionToAllRuns(para.revRich.rawXml, addInsertMarkup, 'insert')), 'inserted'));
      } else if (para.revRich && (para.revRich.text || '').trim()) {
        xml += side('i', asRowXml(`<w:p>${buildRevisionRun(para.revRich.text!, '', 'insert')}</w:p>`));
      }
      return xml;
    }
    return '';
  }

  /** A split or merge piece: its paragraph mark, and the space at the break (addParagraphMarkSpace). */
  const visibleText = (x: string) => [...x.replace(/<w:del\b[\s\S]*?<\/w:del>/g, '').matchAll(/<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>/g)].map(m => m[1]).join('');
  const markSplitOrMerge = (xml: string, para: DiffParagraph, pi: number): string => {
    const kind = para.paraMark!;
    const markChange = recChange('m', { type: kind === 'inserted' ? 'insert' : 'delete', scope: 'mark' });
    // the text as written here, and the next paragraph's (split: the revised one; merge: the original one)
    const n = paragraphs[pi + 1];
    const nextRich = n ? (kind === 'inserted' ? n.revRich : n.origRich) || n.revRich || n.origRich : undefined;
    const next = nextRich?.rawXml ? visibleText(nextRich.rawXml) : (nextRich?.text ?? '');
    // the space only with Fredline's own mark (a mark the draft already revised, kept by Combine, is left
    // alone: recorded as not written by Fredline, as in markup, where marks are not drawn)
    if (addParagraphMarkRevision(xml, kind) === xml) { if (markChange) markChange.written = false; return xml; }
    const here = visibleText(xml);
    // Did the whole paragraph (split: the original; merge: the revised) have a space at the break?
    // "Assessment" + "; and" was "Assessment; and". Look for it in the split/merge group; when it
    // is not at hand, a break in prose is taken to be a space.
    const squash = (t: string) => t.replace(/\s+/g, ' ');
    const tail = squash(here).trimEnd().slice(-24), head = squash(next).trimStart().slice(0, 24);
    let wholeSpace: boolean | null = null;
    for (let j = pi; j >= 0 && j >= pi - 50 && wholeSpace === null; j--) {
      const q = paragraphs[j];
      if (j < pi && !q.paraMark) break;
      const whole = kind === 'inserted' ? q.origRich?.text : q.revRich?.text;
      if (!whole || !tail || !head) continue;
      const w = squash(whole);
      if (w.includes(tail + head)) wholeSpace = false;
      else if (w.includes(tail + ' ' + head)) wholeSpace = true;
    }
    if (wholeSpace === false) return addParagraphMarkRevision(xml, kind);
    return addParagraphMarkRevision(addParagraphMarkSpace(xml, kind, /\s$/.test(here), /^\s/.test(next)), kind);
  };
  for (let pi = 0; pi < paragraphs.length; pi++) {
    const para = paragraphs[pi];
    curPi = pi;
    curRole = '';
    recBlock(pi);

    // Free-tier divider: emit a visible notice paragraph in the DOCX
    if ((para as any)._freeTierDivider) {
      // Close any open table
      if (inTableMode) {
        pushPart('</w:tbl>');
        inTableMode = false;
      }
      // Horizontal rule (thin border-bottom paragraph)
      pushPart(
        '<w:p>' +
          '<w:pPr>' +
            '<w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="E0A000"/></w:pBdr>' +
            '<w:spacing w:after="120"/>' +
          '</w:pPr>' +
        '</w:p>');
      // Notice text
      pushPart(
        '<w:p>' +
          '<w:pPr>' +
            '<w:jc w:val="center"/>' +
            '<w:spacing w:before="120" w:after="120"/>' +
          '</w:pPr>' +
          '<w:r>' +
            '<w:rPr><w:b/><w:color w:val="6D4C00"/><w:sz w:val="22"/></w:rPr>' +
            '<w:t xml:space="preserve">Free comparison limit reached.</w:t>' +
          '</w:r>' +
          runsWithWordmark(' The first ~5 pages were compared above. The rest of the document is shown below without change markup. Get the full version at fredline.app for every page, with no watermark.',
            (t) => `<w:r><w:rPr><w:color w:val="6D4C00"/><w:sz w:val="22"/></w:rPr><w:t xml:space="preserve">${t}</w:t></w:r>`, { size: 11 }) +
        '</w:p>');
      // Horizontal rule (thin border-bottom paragraph)
      pushPart(
        '<w:p>' +
          '<w:pPr>' +
            '<w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="E0A000"/></w:pBdr>' +
            '<w:spacing w:after="200"/>' +
          '</w:pPr>' +
        '</w:p>');
      continue;
    }

    const isRow = isTableRowPara(para);
    if (isRow && !inTableMode) {
      // The opener is streamed as an unclosed fragment, so normalize the tblPr/tblGrid
      // string itself (the part-level normalizer has to skip unclosed table openers).
      pushPart('<w:tbl>' + normalizePropOrder(getTableProps(para)));
      currentTableKey = tableKeyOf(para);
      inTableMode = true;
    } else if (isRow && inTableMode && tableKeyOf(para) !== currentTableKey) {
      // adjacent DIFFERENT table — close the previous one instead of merging
      pushPart('</w:tbl>');
      // The opener is streamed as an unclosed fragment, so normalize the tblPr/tblGrid
      // string itself (the part-level normalizer has to skip unclosed table openers).
      pushPart('<w:tbl>' + normalizePropOrder(getTableProps(para)));
      currentTableKey = tableKeyOf(para);
    } else if (!isRow && inTableMode) {
      pushPart('</w:tbl>');
      inTableMode = false;
    }

    if (isRow) {
      pushPart(emitTableRow(para));
      continue;
    }

    // Handle passthrough blocks
    if (recordOnly && para.type === 'equal' && (para.revRich?.isPassthrough || para.origRich?.isPassthrough)) continue;
    if (para.type === 'equal' && para.revRich && para.revRich.isPassthrough) {
      pushPart(preparePassthrough(para.revRich.rawXml || ''));
      continue;
    }
    if (para.type === 'equal' && para.origRich && para.origRich.isPassthrough) {
      pushPart(preparePassthrough(tagCrossRelIds(para.origRich.rawXml || '')));
      continue;
    }

    // Combine ("track all changes"): if the revised paragraph carries incoming tracked
    // changes and those changes alone explain the original→revised difference (its
    // pre-incoming text equals the original), emit it verbatim so the incoming changes
    // survive under their original authors. A wholly-inserted paragraph that itself
    // contains tracked changes is preserved unconditionally. If the user ALSO edited the
    // paragraph (pre-incoming text no longer matches the original), we fall through to
    // normal re-marking so their change is still tracked — only that paragraph loses the
    // incoming attribution, and nothing is silently left untracked.
    if (combine && para.revRich && hasIncomingRevisions(para.revRich.rawXml)
        && (para.type === 'modify' || para.type === 'para-insert')) {
      // Match the parser's text cleaning (strip zero-width/invisible chars, NBSP->space)
      // so rejected-vs-original text compares equal when the only diff is the incoming edit.
      const norm = (s: string) => s
        .replace(/[​-‏﻿⁠­͏؜]/g, '')
        .replace(/ /g, ' ')
        .replace(/\s+/g, ' ').trim();
      const origText = norm(para.origRich?.text || '');
      const revRejected = norm(rejectedRunText(para.revRich.rawXml!));
      // For a wholly-inserted paragraph, verbatim preservation is only correct
      // when the incoming tracked changes fully explain it (rejecting them leaves
      // nothing) — otherwise its plain runs would ship as untracked baseline text
      // that does not exist in the original, and reject-all would NOT reproduce
      // the original document.
      if ((para.type === 'para-insert' && revRejected === '') || (para.type === 'modify' && origText === revRejected)) {
        // its changes are the draft's own revisions, kept: recorded as not written by Fredline (the
        // summary counts them as the comparison's changes, as the change list lists the draft's)
        if (para.type === 'para-insert') {
          const blank = emptyTrackedBlank(para, diffResult.paragraphs[pi]) && !/<w:sectPr\b/.test(para.revRich.rawXml || '');
          if ((para.text || '').trim() || rawHasEmbed(para.revRich.rawXml) || blank) recWhole(para, 'w', 'insert', false);
        } else {
          recordCarried(para.diff || []);
          if (para.paraMark) recChange('m', { type: para.paraMark === 'inserted' ? 'insert' : 'delete', scope: 'mark', written: false });
        }
        pushPart(para.revRich.rawXml!);
        continue;
      }
    }

    if (para.type === 'equal') {
      const rich = para.revRich || para.origRich;
      if (rich && rich.rawXml) {
        // Track formatting changes on equal-text paragraphs (pPrChange / rPrChange)
        let outputXml: string | null = null;
        // A tab or line break that differs between the drafts: written as an edited paragraph (all
        // its words unchanged), so the change is tracked and Reject All gives the original's back
        // (not in a split or merge: its pieces' whitespace at the break is the paragraph mark's; not
        // in Combine, which keeps the revised draft's unchanged paragraphs as they are)
        // A footnote or endnote mark, a picture, a field or a symbol one draft has and the other does
        // not, in words that are all the same: written as an edited paragraph too, so the mark is
        // tracked (before, a footnote added to an unchanged sentence was copied in unmarked, and
        // its note read as unchanged). Not in Combine, for the same reason.
        const rebuildable = !combine && para.origRich && para.revRich && para.origRich.rawXml && !para.paraMark && !paragraphs[pi - 1]?.paraMark;
        const layout = rebuildable && ((useTrackChanges && layoutDiffers(pieceSide(para.origRich), pieceSide(para.revRich))) || marksDiffer(para.origRich!, para.revRich!))
          ? allEqualDiff(pieceSide(para.origRich), pieceSide(para.revRich)) : null;
        if (layout) outputXml = buildModifiedParagraph({ diff: layout, origRich: para.origRich, revRich: para.revRich });
        else if (useTrackChanges && para.origRich && para.revRich) {
          outputXml = emitEqualParagraphWithFormatChanges(para.origRich, para.revRich);
        }
        if (recordOnly) {
          // only what it records: a formatting change, a split or merge (on the paragraph as it is)
          if (outputXml) pushPart(outputXml);
          if (para.paraMark) markSplitOrMerge(outputXml || rich.rawXml, para, pi);
          continue;
        }
        // preparePassthrough on BOTH paths: the format-change emitter builds from the
        // source paragraph too, so a revised paragraph wholly inside someone's <w:ins>
        // would otherwise carry that revision into a Compare-mode output.
        // (not for a paragraph rebuilt for its layout or marks: that would accept its own tracked changes)
        let xml = layout ? outputXml! : preparePassthrough(outputXml || (para.revRich ? rich.rawXml : tagCrossRelIds(rich.rawXml)));
        // A paragraph split or merge: every word is unchanged, only the paragraph
        // break differs — record it on the paragraph mark, as Word's Compare does.
        if (para.paraMark) xml = markSplitOrMerge(xml, para, pi);

        // Scatter inline watermarks every ~20 unchanged paragraphs
        if (addWatermark && !rich.isTableRow && !rich.isPassthrough && para.text?.trim()) {
          equalParaCount++;
          if (equalParaCount % 20 === 0) {
            pushPart(injectInlineWatermark(xml));
          } else {
            pushPart(xml);
          }
        } else {
          pushPart(xml);
        }
      } else {
        // Plain-text sources (PDF/TXT) have no rawXml. Emit the REVISED wording: an
        // "equal" paragraph may still differ in quote style, and the output represents
        // the revised document.
        let xml = `<w:p><w:r><w:t xml:space="preserve">${xmlEscape(para.revRich?.text ?? para.text!)}</w:t></w:r></w:p>`;
        // a split or merge is recorded on the paragraph mark here too (the summary, the change
        // list and the PDF all count it; the .docx used to show nothing)
        if (para.paraMark) xml = markSplitOrMerge(xml, para, pi);
        pushPart(xml);
      }
    } else if ((para.type === 'para-delete' || para.type === 'para-insert') && useTrackChanges && emptyTrackedBlank(para, diffResult.paragraphs[pi])) {
      // a blank line (empty paragraph) inserted or deleted: only its paragraph mark is revised,
      // so accepting or rejecting adds or removes the blank line as in the drafts
      const raw = para.type === 'para-delete' ? tagCrossRelIds(para.origRich!.rawXml!) : para.revRich!.rawXml!;
      recWhole(para);
      // a page or column break, bookmark, comment or note mark on it is revised with it (left
      // unmarked, Accept All kept a deleted blank line's page break or comment on the paragraph after),
      // and so is whitespace it holds (a space or tab left unmarked went to the start of the paragraph
      // after: Accept All of a deleted " " line before "RENT." gave " RENT.", until October 29, 2026)
      const body = /<w:(?:br\b[^>]*w:type="(?:page|column)"|bookmarkStart\b|commentRangeStart\b|commentReference\b|footnoteReference\b|endnoteReference\b|tab\/>|t\b[^>]*>[^<])/.test(raw)
        ? applyRevisionToAllRuns(raw, para.type === 'para-delete' ? addDeleteMarkup : addInsertMarkup, para.type === 'para-delete' ? 'delete' : 'insert')
        : flattenAcceptRevisions(raw);
      pushPart(markWholeParagraph(body, para.type === 'para-delete' ? 'deleted' : 'inserted', true));
    } else if ((para.type === 'para-delete' || para.type === 'para-insert') && !useTrackChanges && emptyTrackedBlank(para, diffResult.paragraphs[pi])) {
      // markup: a blank line inserted or deleted is not drawn, but the tracked redline revises its
      // mark, and the summary counts it there; recorded as not written
      const raw = para.type === 'para-delete' ? para.origRich?.rawXml : para.revRich?.rawXml;
      if (!/<w:sectPr\b/.test(raw || '')) recWhole(para, 'w', WHOLE[para.type], false);
    } else if (para.type === 'para-delete') {
      if (para.text!.trim() || rawHasEmbed(para.origRich?.rawXml)) {
        recWhole(para);
        if (xmlSkip) continue;
        if (para.origRich && para.origRich.rawXml) {
          pushPart(markWholeParagraph(applyRevisionToAllRuns(tagCrossRelIds(para.origRich.rawXml), addDeleteMarkup, 'delete'), 'deleted', useTrackChanges));
        } else {
          pushPart(markWholeParagraph(`<w:p>${buildRevisionRun(para.text!, '', 'delete')}</w:p>`, 'deleted', useTrackChanges));
        }
      }
    } else if (para.type === 'para-insert') {
      if (para.text!.trim() || rawHasEmbed(para.revRich?.rawXml)) {
        recWhole(para);
        if (xmlSkip) continue;
        if (para.revRich && para.revRich.rawXml) {
          pushPart(markWholeParagraph(applyRevisionToAllRuns(para.revRich.rawXml, addInsertMarkup, 'insert'), 'inserted', useTrackChanges));
        } else {
          pushPart(markWholeParagraph(`<w:p>${buildRevisionRun(para.text!, '', 'insert')}</w:p>`, 'inserted', useTrackChanges));
        }
      }
    } else if (para.type === 'move-from') {
      if (para.text!.trim() || rawHasEmbed(para.origRich?.rawXml)) {
        recWhole(para);
        if (xmlSkip) continue;
        if (para.origRich && para.origRich.rawXml) {
          pushPart(markWholeParagraph(applyRevisionToAllRuns(tagCrossRelIds(para.origRich.rawXml), addMoveFromMarkup, 'move-from'), 'deleted', useTrackChanges));
        } else {
          pushPart(markWholeParagraph(`<w:p>${buildRevisionRun(para.text!, '', 'move-from')}</w:p>`, 'deleted', useTrackChanges));
        }
      }
    } else if (para.type === 'move-to') {
      if (para.text!.trim() || rawHasEmbed(para.revRich?.rawXml)) {
        if ((para as any).diff) {
          // Moved-and-edited: render the internal word-diff (moved tokens stay green,
          // in-transit inserts/deletes show normally). Its paragraph mark goes with the move.
          const built = buildModifiedParagraph(para as any);
          recChange('mt', { type: 'move-to', scope: 'words' });
          pushPart(markWholeParagraph(built, 'inserted', useTrackChanges));
        } else if (para.revRich && para.revRich.rawXml) {
          recWhole(para);
          if (xmlSkip) continue;
          pushPart(markWholeParagraph(applyRevisionToAllRuns(para.revRich.rawXml, addMoveToMarkup, 'move-to'), 'inserted', useTrackChanges));
        } else {
          recWhole(para);
          pushPart(markWholeParagraph(`<w:p>${buildRevisionRun(para.text!, '', 'move-to')}</w:p>`, 'inserted', useTrackChanges));
        }
      }
    } else if (para.type === 'modify' && (para.origRich?.isPassthrough || para.revRich?.isPassthrough)) {
      // A changed passthrough block (content control / TOC): rebuilding it from word
      // tokens would flatten its structure, so show it as a whole-block replacement.
      // A plain-text side (PDF/TXT) has no rawXml — synthesize its paragraph so the text
      // is never dropped from the redline.
      // (the old block and the new one are each a change of their own, and labelled as such)
      curRole = 'd';
      if (para.origRich?.rawXml || (para.origRich?.text || '').trim()) recWhole(para, 'd', 'delete');
      if (xmlSkip) { if (para.revRich?.rawXml || (para.revRich?.text || '').trim()) recWhole(para, 'i', 'insert'); curRole = ''; continue; }
      if (para.origRich?.rawXml) pushPart(applyRevisionToAllRuns(tagCrossRelIds(para.origRich.rawXml), addDeleteMarkup, 'delete'));
      else if ((para.origRich?.text || '').trim()) pushPart(`<w:p>${buildRevisionRun(para.origRich!.text!, '', 'delete')}</w:p>`);
      curRole = 'i';
      if (para.revRich?.rawXml || (para.revRich?.text || '').trim()) recWhole(para, 'i', 'insert');
      if (para.revRich?.rawXml) pushPart(applyRevisionToAllRuns(para.revRich.rawXml, addInsertMarkup, 'insert'));
      else if ((para.revRich?.text || '').trim()) pushPart(`<w:p>${buildRevisionRun(para.revRich!.text!, '', 'insert')}</w:p>`);
      curRole = '';
    } else if (para.type === 'modify') {
      const hasChanges = para.diff!.some(d => d.type !== 'equal' && d.value.trim());
      let xml: string;
      if (!hasChanges) {
        const rich = para.revRich || para.origRich;
        if (rich && rich.rawXml) {
          xml = preparePassthrough(para.revRich ? rich.rawXml : tagCrossRelIds(rich.rawXml));
        } else {
          const text = para.diff!.map(d => d.value).join(' ');
          xml = `<w:p><w:r><w:t xml:space="preserve">${xmlEscape(text)}</w:t></w:r></w:p>`;
        }
      } else {
        // (a split's or merge's piece: its whitespace at the break is the paragraph mark's, so
        // a tab or line break there is not tracked as spacing)
        xml = buildModifiedParagraph(para as any, { spacing: !para.paraMark && !paragraphs[pi - 1]?.paraMark });
      }
      // a split or merge whose piece also carries a word edit: the paragraph mark is revised too
      if (para.paraMark) xml = markSplitOrMerge(xml, para, pi);
      pushPart(xml);
    }
  }

  curPi = -1;
  recBlock(-1);
  if (inTableMode) {
    pushPart('</w:tbl>');
  }

  // Body-level watermark: bottom of document
  if (addWatermark) {
    pushPart(BODY_WATERMARK_BOTTOM);
  }

  return { bodyParts, formatBlocks, push: pushPart };
}
